"use client"

import * as React from "react"
import { LuChevronDown, LuChevronLeft, LuChevronRight } from "react-icons/lu"
import { DayPicker, type DropdownProps } from "react-day-picker"

import { cn } from "../../utils/cn"

export type CalendarProps = React.ComponentProps<typeof DayPicker>

function CalendarDropdown({
  className,
  value,
  onChange,
  children,
  ...props
}: DropdownProps) {
  return (
    <div className={cn("relative", className)}>
      <select
        name={props.name}
        aria-label={props["aria-label"]}
        className="h-8 w-full appearance-none rounded-[var(--radius-input)] border border-border-primary bg-transparent py-0 pl-2.5 pr-8 text-sm font-medium text-text-primary transition-colors focus:bg-background-primary focus:outline-none disabled:cursor-not-allowed disabled:opacity-50"
        value={value}
        onChange={onChange}
      >
        {children}
      </select>
      <LuChevronDown className="pointer-events-none absolute right-2.5 top-1/2 size-3.5 -translate-y-1/2 text-text-secondary" />
    </div>
  )
}

function Calendar({
  className,
  classNames,
  showOutsideDays = true,
  ...props
}: CalendarProps) {
  return (
    <DayPicker
      showOutsideDays={showOutsideDays}
      className={cn("p-3", className)}
      captionLayout="dropdown"
      fromYear={1900}
      toYear={new Date().getFullYear()}
      classNames={{
        vhidden: "sr-only",
        months: "flex flex-col gap-4 sm:flex-row sm:gap-4",
        month: "space-y-4",
        caption: "relative flex items-center justify-center pt-1",
        caption_label: "text-sm font-medium text-text-primary",
        caption_dropdowns: "flex items-center gap-1.5",
        dropdown_month: "w-[8rem]",
        dropdown_year: "w-[6rem]",
        nav: "pointer-events-none absolute inset-x-0 top-1 flex items-center justify-between",
        nav_button:
          "pointer-events-auto inline-flex size-8 items-center justify-center border border-border-primary bg-background-primary p-0 text-text-primary transition-colors hover:bg-background-secondary focus:outline-none disabled:cursor-not-allowed disabled:opacity-50",
        nav_button_previous: "ml-1",
        nav_button_next: "mr-1",
        table: "w-full border-collapse space-y-1",
        head_row: "flex",
        head_cell:
          "w-9 rounded-md text-[0.8rem] font-normal text-text-secondary",
        row: "flex w-full mt-2",
        cell: "h-9 w-9 text-center text-sm p-0 relative rounded-[var(--radius-input)] [&:has([aria-selected].day-range-end)]:rounded-r-[var(--radius-input)] [&:has([aria-selected].day-outside)]:bg-background-secondary/50 [&:has([aria-selected])]:bg-background-secondary first:[&:has([aria-selected])]:rounded-l-[var(--radius-input)] last:[&:has([aria-selected])]:rounded-r-[var(--radius-input)] focus-within:relative focus-within:z-20",
        day: "inline-flex size-9 items-center justify-center rounded-[var(--radius-input)] p-0 font-normal text-text-primary transition-colors hover:bg-background-secondary focus:outline-none disabled:cursor-not-allowed disabled:opacity-50 aria-selected:opacity-100",
        day_range_end: "day-range-end",
        day_selected:
          "rounded-[var(--radius-input)] bg-[var(--color-primary)] text-[var(--neutral-lightest)] hover:bg-[var(--color-primary-hover)] hover:text-[var(--neutral-lightest)] focus:bg-[var(--color-primary-hover)] focus:text-[var(--neutral-lightest)]",
        day_today: "border border-border-primary bg-background-primary text-text-primary",
        day_outside:
          "day-outside text-text-secondary aria-selected:bg-background-secondary/50 aria-selected:text-text-primary",
        day_disabled: "text-text-secondary opacity-50",
        day_range_middle:
          "aria-selected:bg-background-secondary aria-selected:text-text-primary",
        day_hidden: "invisible",
        ...classNames,
      }}
      components={{
        Dropdown: CalendarDropdown,
        IconLeft: ({ className, ...props }) => (
          <LuChevronLeft className={cn("h-4 w-4", className)} {...props} />
        ),
        IconRight: ({ className, ...props }) => (
          <LuChevronRight className={cn("h-4 w-4", className)} {...props} />
        ),
      }}
      {...props}
    />
  )
}
Calendar.displayName = "Calendar"

export { Calendar }
